import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { enUS, es, fr } from 'date-fns/locale';

import {
  INCENTIVES_MARKET_CONTRACT_UNIT_TAGS,
  LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS,
} from 'modules/incentivesMarket/constants/incentivesMarketContractTemplateTags';
import {
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';
import {
  replaceIncentivesMarketContractTag,
  replaceIncentivesMarketContractUnitTag,
} from 'modules/incentivesMarket/utils/replaceIncentivesMarketContractTag';
import { formatCurrencyDecimals, formatNumber } from 'utils/formats';

import { getPublisherAddressLine } from './getPublisherAddressLine';

const DATE_LOCALES: Record<string, Locale> = {
  es,
  en: enUS,
  fr,
};

const PENDING = '-';

export type ApplyIncentivesMarketAcceptOfferContractPreviewTagsArgs = {
  html: string;
  language: string;
  operationReferencePlaceholder: string;
  offerType: IncentiveOfferType;
  publisherPartner: Partial<IncentivesMarketBeneficiary> | null;
  participantPartner: Partial<IncentivesMarketBeneficiary> | null;
  purchaseQuantity: number;
  unitPrice: number;
  totalPrice?: number;
  className: string;
};

export const applyIncentivesMarketAcceptOfferContractPreviewTags = ({
  html,
  language,
  operationReferencePlaceholder,
  offerType,
  publisherPartner,
  participantPartner,
  purchaseQuantity,
  unitPrice,
  totalPrice: totalPriceOverride,
  className,
}: ApplyIncentivesMarketAcceptOfferContractPreviewTagsArgs): string => {
  const langKey = language?.split('-')[0] || 'es';
  const dateLocale = DATE_LOCALES[langKey] || es;

  const isSell = offerType === IncentiveOfferType.SELL;
  const seller = isSell ? publisherPartner : participantPartner;
  const buyer = isSell ? participantPartner : publisherPartner;

  const sellerName = seller?.name?.trim() || PENDING;
  const sellerCif = seller?.cif?.trim() || PENDING;
  const sellerAddress = getPublisherAddressLine(seller) || PENDING;
  const sellerIban = seller?.bankAccountNumber?.trim() || PENDING;
  const sellerAccountHolder = sellerName;

  const buyerName = buyer?.name?.trim() || PENDING;
  const buyerCif = buyer?.cif?.trim() || PENDING;
  const buyerAddress = getPublisherAddressLine(buyer) || PENDING;

  const totalPrice = totalPriceOverride ?? purchaseQuantity * unitPrice;
  const shareClassLabel = className?.trim() || PENDING;

  let result = html;

  result = replaceIncentivesMarketContractTag(result, 'MERCADO_NOMBRE_VENDEDOR', sellerName);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DNI_VENDEDOR', sellerCif);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DOMICILIO_VENDEDOR', sellerAddress);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_IBAN_VENDEDOR', sellerIban);
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_TITULAR_CUENTA_VENDEDOR',
    sellerAccountHolder
  );

  result = replaceIncentivesMarketContractTag(result, 'MERCADO_NOMBRE_COMPRADOR', buyerName);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DNI_COMPRADOR', buyerCif);
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_DOMICILIO_COMPRADOR', buyerAddress);

  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.NUM,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.NUM,
    formatNumber(purchaseQuantity) || PENDING
  );
  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.CLASS,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.CLASS,
    shareClassLabel
  );
  result = replaceIncentivesMarketContractUnitTag(
    result,
    INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.PRICE,
    LEGACY_INCENTIVES_MARKET_CONTRACT_UNIT_TAGS.PRICE,
    formatCurrencyDecimals(unitPrice) || PENDING
  );
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_PRECIO_TOTAL',
    formatCurrencyDecimals(totalPrice) || PENDING
  );

  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_FECHA',
    format(new Date(), 'PPP', { locale: dateLocale })
  );
  result = replaceIncentivesMarketContractTag(result, 'MERCADO_HORA', format(new Date(), 'HH:mm'));
  result = replaceIncentivesMarketContractTag(
    result,
    'MERCADO_REFERENCIA_OPERACION',
    operationReferencePlaceholder
  );

  return result;
};
